import React from "react";
import { Path, useForm } from "react-hook-form";
import { RowFormText } from "../Common/Forms/FormText";
import KaAlert from "../Common/KaAlert";
import KaModal from "../Common/KaModal";
import SpinnerButton from "../Common/SpinnerButton";
import useDisplayMessage from "../Common/useDisplayMessage";
import { ExternalIntegrationDto } from "../../requests/useGetExternalIntegrations";
import useRegisterExternalIntegration, { ExternalIntegrationRegisteredDto, UpsertExternalIntegrationDto } from "../../requests/useRegisterExternalIntegration";
import useUpdateExternalIntegration from "../../requests/useUpdateExternalIntegration";

interface CreateEditExternalIntegrationProps {
  initial: ExternalIntegrationDto | null;
  onDismiss: () => void;
  onRegistered: (registered: ExternalIntegrationRegisteredDto) => void;
  onEdited: (name: string) => void;
}

export const CreateEditExternalIntegration = (props: CreateEditExternalIntegrationProps) => {
  const isEdit = props.initial !== null;
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm<UpsertExternalIntegrationDto>({
    defaultValues: { name: props.initial?.name ?? '' }
  });

  const registerIntegration = useRegisterExternalIntegration({
    onSuccess: registered => props.onRegistered(registered),
    onError: e => displayMessage.fail(e.message),
  });

  const update = useUpdateExternalIntegration(props.initial?.clientId ?? '', {
    onSuccess: () => props.onEdited(getValues().name ?? ''),
    onError: e => displayMessage.fail(e.message),
  });

  const onSave = (values: UpsertExternalIntegrationDto) => {
    if (isEdit) {
      update.request(values);
    } else {
      registerIntegration.request(values);
    }
  };

  const hookProps = (name: Path<UpsertExternalIntegrationDto>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name,
    setValue,
    register,
    errors,
  });

  return <KaModal
    onHide={props.onDismiss}
    show={true}
    title={isEdit ? 'Edit external integration' : 'Register external integration'}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
    </>)}
    footer={(
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={isEdit ? update.isLoading : registerIntegration.isLoading}
        onClick={handleSubmit(onSave)}>
        {isEdit ? 'Save' : 'Register'}
      </SpinnerButton>
    )}
  />
};
